import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  layout,
} from '../../style-utils/theme';
import { up } from '../../style-utils/media-queries';
import { Staleable, StateSwitch, listItemMotion, m } from '../../motion';
import {
  ListSkeleton,
  skeletonHeight,
} from '../pages/dashboard/dashboard-shared-components/load-state';
import {
  NotificationPreference,
  getPreferences,
  resetPreference,
  setPreference,
} from '../../api/notifications-client';
import {
  PUSH_SW_PATH,
  PushState,
  enablePushOnThisDevice,
  probePushState,
} from './push-device';

/**
 * Account › Notifications.
 *
 * Shows only the event types this person can actually RECEIVE — the server
 * derives that list from their role and their residency, so a concierge never
 * sees a switch for billing and a resident never sees one for the roster.
 * Nothing here has to know the catalog.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN "OFF" SWITCH ACTUALLY DOES
 * ---------------------------------------------------------------------------
 * Turning a channel off suppresses DELIVERY, not the record: the notification
 * is still written and still appears in the bell's history, greyed, and simply
 * stops counting toward the badge and stops being emailed or pushed. That is
 * the product decision, and the copy below says so, because a switch labelled
 * only "off" would reasonably be read as "this will never be recorded".
 */

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const Intro = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  line-height: ${typography.leading.normal};
  max-width: 60ch;
`;

const DeviceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.md};
  }
`;

const DeviceCopy = styled.div`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  line-height: ${typography.leading.normal};

  span {
    display: block;
    font-size: ${typography.size.xs}px;
    color: ${palette.slate};
  }
`;

const EnableButton = styled.button`
  height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${palette.lagoon};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  white-space: nowrap;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisDark};
  }

  &:disabled {
    background: ${palette.lineStrong};
    color: ${palette.slateLight};
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const Table = styled.div`
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  overflow: hidden;
`;

/* Below md this collapses to a stacked card per row — design system rule 4,
   "tables reflow to cards" — because three toggles plus a label never fit a
   phone width as columns. */
const HeadRow = styled.div`
  display: none;

  ${up('md')} {
    display: grid;
    grid-template-columns: 1fr repeat(3, 88px);
    gap: ${spacing.sm};
    align-items: center;
    padding: ${spacing.xs} ${spacing.md};
    background: ${palette.surfaceMuted};
    border-bottom: 1px solid ${palette.line};
    font-family: ${typography.ui};
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${palette.slate};
  }
`;

/* A motion row: the table spreads listItemMotion(index) on it (no `layout`,
   the rows never reorder). */
const Row = m.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border-bottom: 1px solid ${palette.line};

  &:last-of-type {
    border-bottom: none;
  }

  ${up('md')} {
    display: grid;
    grid-template-columns: 1fr repeat(3, 88px);
    gap: ${spacing.sm};
    align-items: center;
  }
`;

const RowLabel = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-family: ${typography.ui};
    font-size: ${typography.size.sm}px;
    font-weight: ${typography.weight.medium};
    color: ${palette.ink};
    line-height: ${typography.leading.snug};
  }

  span {
    display: block;
    margin-top: ${spacing.half};
    font-family: ${typography.ui};
    font-size: ${typography.size.xs}px;
    color: ${palette.slate};
    line-height: ${typography.leading.normal};
  }
`;

const Toggles = styled.div`
  display: flex;
  gap: ${spacing.sm};

  ${up('md')} {
    display: contents;
  }
`;

const Toggle = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${({ disabled }) => (disabled ? palette.slateLight : palette.slate)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  input {
    width: 20px;
    height: 20px;
    accent-color: ${palette.lagoon};
    cursor: inherit;
  }

  ${up('md')} {
    justify-content: center;
    /* The column header carries the channel name on wide screens. */
    span {
      display: none;
    }
  }
`;

const ResetButton = styled.button`
  align-self: flex-start;
  border: none;
  background: transparent;
  padding: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.irisDark};
  cursor: pointer;
  text-decoration: underline;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const NotificationPreferences: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [pushAvailable, setPushAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [pushState, setPushState] = useState<PushState>('unsupported');
  const [enabling, setEnabling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPreferences();
      setPrefs(data.preferences);
      setPushAvailable(data.push_available);
    } catch {
      toast.error('Could not load your notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // What can this BROWSER do, and is push already on here for THIS person?
  // The probe (push-device.ts) re-registers an existing endpoint under the
  // current session before calling it "enabled" — the shared-tablet reason is
  // written up there, next to the code that acts on it.
  useEffect(() => {
    let alive = true;

    probePushState().then(state => {
      if (alive) setPushState(state);
    });

    return () => {
      alive = false;
    };
  }, []);

  /**
   * TURNING IT OFF — the half that was missing.
   *
   * Push could be enabled from this card and never disabled: once `pushState`
   * was 'enabled' the only control read "Enabled" and was permanently disabled.
   * Nothing anywhere called `pushManager.unsubscribe()`, so a resident who
   * changed their mind, or a concierge handing a shared tablet to the next
   * shift, had no way to stop delivery to that device short of clearing site
   * data in browser settings.
   *
   * The server row cleans itself up: the endpoint is dead the moment the
   * browser unsubscribes, and the next send comes back 404/410, which push.js
   * already treats as gone for good.
   */
  const disablePush = useCallback(async () => {
    setEnabling(true);
    try {
      const registration =
        await navigator.serviceWorker.getRegistration(PUSH_SW_PATH);
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      setPushState('ready');
      toast.success('Push notifications turned off for this device');
    } catch {
      toast.error('Could not turn push off for this device');
    } finally {
      setEnabling(false);
    }
  }, []);

  /**
   * Turn on push for THIS browser — the shared flow in push-device.ts, behind
   * an explicit button because a permission asked for without a gesture is
   * one the browser may block for good. The outcome comes back as a word; what
   * this screen says about each one is unchanged.
   */
  const enablePush = useCallback(async () => {
    setEnabling(true);
    try {
      const result = await enablePushOnThisDevice();

      if (result === 'enabled') {
        setPushState('enabled');
        toast.success('Push notifications are on for this device');
      } else if (result === 'denied' || result === 'dismissed') {
        setPushState(result === 'denied' ? 'denied' : 'ready');
        toast.error('Notifications were not allowed for this browser');
      } else if (result === 'not-configured') {
        toast.error('Push is not configured on the server');
      } else {
        toast.error('Could not turn on push for this device');
      }
    } finally {
      setEnabling(false);
    }
  }, []);

  const update = useCallback(
    async (pref: NotificationPreference, channel: 'in_app' | 'email' | 'push') => {
      const next = {
        in_app: pref.in_app,
        email: pref.email,
        push: pref.push,
        [channel]: !pref[channel],
      } as { in_app: boolean; email: boolean; push: boolean };

      setSaving(pref.event_type);
      // Optimistic, then corrected from the response. A settings toggle that
      // waits for a round trip before moving feels broken on a phone.
      setPrefs(current =>
        current.map(p =>
          p.event_type === pref.event_type
            ? { ...p, ...next, source: 'override' }
            : p
        )
      );
      try {
        const saved = await setPreference(pref.event_type, next);
        setPrefs(current =>
          current.map(p => (p.event_type === saved.event_type ? { ...p, ...saved } : p))
        );
      } catch {
        toast.error('Could not save that setting');
        load();
      } finally {
        setSaving(null);
      }
    },
    [load]
  );

  const reset = useCallback(
    async (pref: NotificationPreference) => {
      try {
        await resetPreference(pref.event_type);
        await load();
      } catch {
        toast.error('Could not reset that setting');
      }
    },
    [load]
  );

  /* THREE STATES, ONE BOX. The loading sentence is a skeleton the shape of
     the rows it precedes; StateSwitch crossfades it, the empty line and the
     settings inside one container. A reload with settings on screen (after a
     reset, or a save that failed) is a refresh: the rows stay and dim. */
  const stateKey =
    loading && prefs.length === 0
      ? 'loading'
      : prefs.length === 0
        ? 'empty'
        : 'ready';

  return (
    <StateSwitch state={stateKey} minHeight={skeletonHeight(4, 72)}>
      {stateKey === 'loading' ? (
        <ListSkeleton rows={4} rowHeight={72} label="notification settings" />
      ) : stateKey === 'empty' ? (
        <Intro>There are no notification settings for your account yet.</Intro>
      ) : (
    <Staleable stale={loading}>
    <Wrap>
      <Intro>
        Choose how you hear about each thing. Turning a channel off stops the
        delivery, not the record — anything switched off still appears in your
        bell&rsquo;s history, it just won&rsquo;t count as unread, email you, or
        buzz your phone.
      </Intro>

      {pushAvailable ? (
        <DeviceCard>
          <DeviceCopy>
            Push notifications on this device
            <span>
              {pushState === 'enabled'
                ? 'This browser is registered and can receive push notifications.'
                : pushState === 'denied'
                  ? 'This browser has blocked notifications. Allow them in your browser settings, then come back.'
                  : pushState === 'unsupported'
                    ? 'This browser cannot receive push notifications. On iPhone, add Lobbero to your home screen first.'
                    : 'Turn on push so parcels and arrivals reach you when the app is closed.'}
            </span>
          </DeviceCopy>
          <EnableButton
            type="button"
            onClick={pushState === 'enabled' ? disablePush : enablePush}
            disabled={
              enabling ||
              pushState === 'unsupported' ||
              pushState === 'denied'
            }
          >
            {enabling
              ? pushState === 'enabled'
                ? 'Turning off…'
                : 'Turning on…'
              : pushState === 'enabled'
                ? 'Turn off push'
                : 'Turn on push'}
          </EnableButton>
        </DeviceCard>
      ) : (
        /* PUSH IS OFF FOR THE WHOLE DEPLOYMENT, AND THAT NEEDS SAYING.
           Hiding the card left the disabled Push column with no explanation
           anywhere on the screen: a reader whose switches will not move has no
           way to tell a broken browser from a server that was never set up for
           push, and the second is not something they can fix. */
        <DeviceCard>
          <DeviceCopy>
            Push notifications are not set up
            <span>
              Push is not enabled on this Lobbero account, so the Push column
              below is switched off for everyone. Email and in-app alerts are
              unaffected. Ask your administrator if you need push.
            </span>
          </DeviceCopy>
        </DeviceCard>
      )}

      <Table>
        <HeadRow>
          <span>Notify me when</span>
          <span style={{ textAlign: 'center' }}>In app</span>
          <span style={{ textAlign: 'center' }}>Email</span>
          <span style={{ textAlign: 'center' }}>Push</span>
        </HeadRow>

        {prefs.map((pref, index) => (
          <Row
            key={pref.event_type}
            {...listItemMotion(index)}
            layout={false}
          >
            <RowLabel>
              <strong>{pref.label}</strong>
              <span>{pref.description}</span>
              {pref.source === 'override' ? (
                <ResetButton type="button" onClick={() => reset(pref)}>
                  Reset to default
                </ResetButton>
              ) : null}
            </RowLabel>

            <Toggles>
              <Toggle>
                <input
                  type="checkbox"
                  checked={pref.in_app}
                  disabled={saving === pref.event_type}
                  onChange={() => update(pref, 'in_app')}
                  aria-label={`${pref.label} — in app`}
                />
                <span>In app</span>
              </Toggle>
              <Toggle>
                <input
                  type="checkbox"
                  checked={pref.email}
                  disabled={saving === pref.event_type}
                  onChange={() => update(pref, 'email')}
                  aria-label={`${pref.label} — email`}
                />
                <span>Email</span>
              </Toggle>
              {/* Left interactive even when this browser has no subscription:
                  the preference is per PERSON and their phone may already be
                  registered. Disabling it here would let one laptop silence a
                  channel the account holder set up on another device. */}
              <Toggle disabled={!pushAvailable}>
                <input
                  type="checkbox"
                  checked={pref.push && pushAvailable}
                  disabled={!pushAvailable || saving === pref.event_type}
                  onChange={() => update(pref, 'push')}
                  aria-label={`${pref.label} — push`}
                />
                <span>Push</span>
              </Toggle>
            </Toggles>
          </Row>
        ))}
      </Table>
    </Wrap>
    </Staleable>
      )}
    </StateSwitch>
  );
};
